import { useMemo, useState } from 'react';
import {
  useGetStudentSummaryQuery,
  useGetStudentListQuery,
  type StudentListParams,
} from '@/entities/Dashboard';
import { CustomInput } from '@/shared/ui/Fields';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import SearchIcon from '@/shared/assets/icons/search.svg';
import UsersIcon from '@/shared/assets/icons/dashboard/Users.svg';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import { buildMetric } from '../../../lib/metrics';
import {
  formatInactive,
  formatGrade,
  formatPercentOrDash,
  formatStudentSubtitle,
} from '../../../lib/rows';
import {
  DASHBOARD_DATE_PERIOD_OPTIONS,
  toDashboardDateParam,
  type DashboardDatePeriod,
} from '../../../lib/datePeriod';
import type { MetricItem } from '../../../config/mockDashboardData';
import { AnalyticsMetricsGrid } from '../../components/AnalyticsMetricCard';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { AnalyticsPagination, AnalyticsSubTabs } from '../../components/AnalyticsTable';
import styles from '../../dashboard/Dashboard.module.scss';

const SUB_TABS = [
  { key: 'all', label: 'All students' },
  { key: 'top', label: 'Top Performing Students' },
  { key: 'risk', label: 'Students At Risk' },
];

const PAGE_SIZE = 8;

interface StudentAnalyticsTabProps {
  onExport: () => void;
}

export const StudentAnalyticsTab = ({ onExport }: StudentAnalyticsTabProps) => {
  const [datePeriod, setDatePeriod] = useState<DashboardDatePeriod>('this-month');
  const [subTab, setSubTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search.trim(), 500);
  const date = toDashboardDateParam(datePeriod);

  const { data: summary } = useGetStudentSummaryQuery({ date });

  const performanceStatus =
    subTab === 'risk' ? 'at_risk' : subTab === 'top' ? 'top' : undefined;

  const listParams = useMemo<StudentListParams>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
      performanceStatus,
    }),
    [page, debouncedSearch, performanceStatus],
  );

  const { data: list } = useGetStudentListQuery(listParams);

  const metrics = useMemo<MetricItem[]>(() => {
    const s = summary?.data;
    return [
      buildMetric('1', 'Active Students', s?.activeStudents, 'count'),
      buildMetric('2', 'Students At Risk', s?.studentsAtRisk, 'count'),
      buildMetric('3', 'Avg. Grade (GPA)', s?.courseAverageGrade, 'grade'),
      buildMetric('4', 'Avg. Courses Completion Rate', s?.courseCompletionRate, 'percent'),
      buildMetric('5', 'Dropout Rate', s?.dropoutRate, 'percent'),
      buildMetric('6', 'Course Diploma Rate', s?.courseDiplomaRate, 'percent'),
    ];
  }, [summary]);

  const rows = list?.data?.students ?? [];
  const pagination = list?.data?.pagination;

  const handleSubTab = (key: string) => {
    setSubTab(key);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Student Analytics"
          icon={<Svg Icon={UsersIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          onExport={onExport}
          periodOptions={DASHBOARD_DATE_PERIOD_OPTIONS}
          periodValue={datePeriod}
          onPeriodChange={(key) => setDatePeriod(key as DashboardDatePeriod)}
        />
        <AnalyticsMetricsGrid metrics={metrics} columns={6} />
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Student Performance Insights"
          icon={<Svg Icon={UsersIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          showToolbar={false}
        />
        <AnalyticsSubTabs tabs={SUB_TABS} value={subTab} onChange={handleSubTab} />
        <CustomInput aria-label="Search"
          className={searchInputClassName}
          startContent={<Svg Icon={SearchIcon} />}
          placeholder="Search"
          value={search}
          onValueChange={handleSearch}
        />
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className="sr-only">Student performance insights</caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">ID</th>
                <th scope="col">Students</th>
                <th scope="col">Current progression</th>
                <th scope="col">Avg. Grade</th>
                <th scope="col">Attendance</th>
                <th scope="col">Inactive</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.enrollmentId}>
                  <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                  <td>{row.enrollmentId}</td>
                  <td>
                    <div className={styles.tableStudentCell}>
                      <span className={styles.tableStudentName}>
                        {row.user ? `${row.user.firstName} ${row.user.lastName}` : '—'}
                      </span>
                      <span className={styles.tableStudentSub}>
                        {formatStudentSubtitle(row.course, row.intake)}
                      </span>
                    </div>
                  </td>
                  <td className={row.performanceStatus === 'at_risk' ? styles.atRisk : undefined}>
                    <div className={styles.progressCell}>
                      <span>{formatPercentOrDash(row.courseProgressByModules)} modules</span>
                      <span className={styles.progressSub}>
                        {formatPercentOrDash(row.courseProgressByLessons)} lessons
                      </span>
                    </div>
                  </td>
                  <td className={row.performanceStatus === 'at_risk' ? styles.atRisk : undefined}>
                    {formatGrade(row.courseAverageGrade)}
                  </td>
                  <td className={row.performanceStatus === 'at_risk' ? styles.atRisk : undefined}>
                    {formatPercentOrDash(row.attendance)}
                  </td>
                  <td className={row.performanceStatus === 'at_risk' ? styles.atRisk : undefined}>
                    {formatInactive(row.lastSeenAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AnalyticsPagination
          page={pagination?.page ?? page}
          pageCount={pagination?.pageCount ?? 0}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
};
